import React, { useRef } from 'react';
import { Form, Input, InputNumber, Select } from 'antd';
import LocationPicker from '@/components/locationpicker';
import { coordinatePair } from '@/utils/coordinates';

const PostLocationField = props => {
  const { status, errors, warnings } = Form.Item.useStatus();
  const messages = useRef({ errors: [], warnings: [] });
  // 输入时的异步校验会暂时清空错误，保留上一条提示避免文字闪动。
  if (status !== 'validating') messages.current = { errors, warnings };
  return <LocationPicker {...props}
    feedback={<div className="location-picker-feedback" aria-live="polite">
      {messages.current.errors.map((error, index) => <p className="location-picker-error" key={index}>{error}</p>)}
      {messages.current.warnings.map((warning, index) => <p className="location-picker-warning" key={index}>{warning}</p>)}
    </div>}
    guidance={props.defaultCurrentLocation ? '默认使用当前位置；选择其他活动地点后，会改用所选地点匹配附近招募。' : props.compact ? '点击“选择活动地点”可搜索或在地图上更换集合地点，选中后自动设置。' : '输入地点后，请选择搜索结果、在地图上选点或使用当前位置确认位置。隐藏位置也需要确认地点。'} />;
};

export const postOptionsPayload = values => ({
  post_type: values.post_type || 'general',
  offered_skills: values.post_type === 'skill_exchange' ? values.offered_skills?.trim() || '' : '',
  wanted_skills: values.post_type === 'skill_exchange' ? values.wanted_skills?.trim() || '' : '',
  location_visibility: values.location_visibility || 'hidden',
  latitude: values.latitude ?? null, longitude: values.longitude ?? null,
});

const PostOptions = ({ form, locked = false, disabled = false, onBusyChange, defaultCurrentLocation = false, publishLayout = false }) => {
  const locationFirst = defaultCurrentLocation || publishLayout;
  const type = Form.useWatch('post_type', form);
  const visibility = Form.useWatch('location_visibility', form);
  const latitude = Form.useWatch('latitude', form);
  const longitude = Form.useWatch('longitude', form);
  const coordinatesRequired = defaultCurrentLocation || visibility === 'approximate' || visibility === 'exact';
  const coordinatesRule = () => {
    const latitude = form.getFieldValue('latitude'); const longitude = form.getFieldValue('longitude');
    const hasCoordinates = latitude != null || longitude != null;
    const hasLocation = Boolean(form.getFieldValue('location')?.trim());
    const publicLocation = ['approximate', 'exact'].includes(form.getFieldValue('location_visibility'));
    return (!(defaultCurrentLocation || publicLocation || hasLocation || hasCoordinates) || coordinatePair(longitude, latitude))
      ? Promise.resolve() : Promise.reject(new Error('请搜索并选择地点、在地图上选点或使用当前位置，以确定地图位置'));
  };
  const locationScope = <Form.Item name="location_visibility" label="位置公开范围" extra="位置用于附近招募匹配。隐藏位置不公开地图位置和距离，也不参与热力图；手动选择的地点文字仍会展示。">
    <Select options={[{ value: 'hidden', label: '隐藏位置' }, { value: 'approximate', label: '大致位置' }, { value: 'exact', label: '精确位置' }]} />
  </Form.Item>;
  const recruitmentType = <>
    <Form.Item name="post_type" label="招募类型"><Select disabled={locked} options={[{ value: 'general', label: '普通招募' }, { value: 'skill_exchange', label: '技能交换' }]} /></Form.Item>
    {type === 'skill_exchange' && <>
      <Form.Item name="offered_skills" label="我可以提供的技能" rules={[{ required: true, whitespace: true, message: '请填写你能提供的技能' }]}><Input.TextArea disabled={locked} maxLength={500} showCount autoSize={{ minRows: 2, maxRows: 6 }} /></Form.Item>
      <Form.Item name="wanted_skills" label="我希望交换的技能" rules={[{ required: true, whitespace: true, message: '请填写希望学习的技能' }]}><Input.TextArea disabled={locked} maxLength={500} showCount autoSize={{ minRows: 2, maxRows: 6 }} /></Form.Item>
    </>}
  </>;
  return <>
    {locationFirst ? locationScope : recruitmentType}
    {!locationFirst && locationScope}
    <Form.Item className="post-form-location-field" name="location" label="活动地点" valuePropName="location" trigger="onLocationChange" validateTrigger="onLocationChange" required={coordinatesRequired} dependencies={['latitude', 'longitude', 'location_visibility']} rules={[{ validator: coordinatesRule }]} help="">
      <PostLocationField longitude={longitude} latitude={latitude} defaultCurrentLocation={defaultCurrentLocation} compact={locationFirst} disabled={disabled} onBusyChange={onBusyChange} onChange={values => {
        const location = values.location?.slice(0, 200);
        form.setFieldsValue({ ...values, ...(location ? { location } : {}) });
      }} />
    </Form.Item>
    {locationFirst && recruitmentType}
    <Form.Item name="latitude" hidden><InputNumber /></Form.Item>
    <Form.Item name="longitude" hidden><InputNumber /></Form.Item>
  </>;
};
export default PostOptions;
